import { io } from 'socket.io-client';

/** Match display app dev proxy: WebSocket via Vite :3000 → :3010 when using local backend. */
function getConnectUrl() {
  const envUrl = (import.meta.env.VITE_SOCKET_URL || '').trim();
  if (import.meta.env.DEV) {
    const effective = envUrl || 'http://localhost:3010';
    if (
      effective.includes('localhost:3010') ||
      effective.includes('127.0.0.1:3010')
    ) {
      if (typeof window !== 'undefined' && window.location?.origin) {
        return window.location.origin;
      }
    }
  }
  return envUrl || 'http://localhost:3010';
}

/**
 * The socket's `auth` (#1226). socket.io calls it on every connect and
 * reconnect, so each connection carries a fresh one-time ticket for the
 * sessions this browser holds. The ticket comes from the display's own
 * origin — not getConnectUrl() — because only there do the session cookies
 * travel; the socket itself never sees them. No session: ticket null, and the
 * socket connects as anonymous.
 */
export function socketAuth(extra = {}) {
  return (cb) => {
    fetch('/api/auth/socket-ticket', { method: 'POST', credentials: 'include' })
      .then((res) => (res.ok ? res.json() : {}))
      .then((body) => cb({ ...extra, ticket: body?.ticket || null }))
      .catch(() => cb({ ...extra, ticket: null }));
  };
}

class SocketService {
  constructor() {
    this.socket = null;
    this.sessionId = null;
    this.clientType = null;
    this.participantId = null;
    this.heartbeatInterval = null;
    this.visibilityChangeListener = null;
    
    // Expose to global window for debugging
    if (typeof window !== 'undefined') {
      window.SocketService = this;
    }
  }

  // Method to check connection status
  isConnected() {
    return this.socket && this.socket.connected;
  }

  connect(sessionId, clientType, participantId = null) {
    // If already connected, just update session info and rejoin
    if (this.socket && this.socket.connected) {
      console.log('🔌 Already connected, rejoining session...');
      this.sessionId = sessionId;
      this.clientType = clientType;
      this.participantId = participantId;
      this.socket.emit('join-session', {
        sessionId: this.sessionId,
        clientType: this.clientType,
        participantId: this.participantId
      });
      return this.socket;
    }

    // If socket exists and is still connecting, just return it (don't recreate)
    if (this.socket && !this.socket.connected && !this.socket.disconnected) {
      console.log('🔌 Socket still connecting, reusing...');
      this.sessionId = sessionId;
      this.clientType = clientType;
      this.participantId = participantId;
      return this.socket;
    }

    // If there's an existing socket that's truly disconnected, clean it up
    if (this.socket && this.socket.disconnected) {
      console.log('🔌 Cleaning up disconnected socket...');
      this.socket.disconnect();
      this.socket = null;
    }

    this.sessionId = sessionId;
    this.clientType = clientType;
    this.participantId = participantId;

    const connectUrl = getConnectUrl();
    console.log('🌐 Connecting to:', connectUrl);
    console.log('🌐 Environment VITE_SOCKET_URL:', import.meta.env.VITE_SOCKET_URL);
    console.log('🌐 User Agent:', navigator.userAgent);
    console.log('🌐 Is Mobile:', /Mobi|Android/i.test(navigator.userAgent));
    
    // Test basic connectivity first
    if (/Mobi|Android/i.test(navigator.userAgent)) {
      this.testConnectivity(connectUrl);
    }
    
    // Only create new connection if none exists
    if (!this.socket) {
      console.log('🔌 Creating new socket connection...');
      // Get persistent device ID for tracking
      const deviceId = typeof localStorage !== 'undefined' && localStorage.getItem('kuutio-device-id') || null;

      this.socket = io(connectUrl, {
        transports: ['polling', 'websocket'], // Try polling first
        timeout: 20000, // Increase timeout for mobile
        reconnection: true, // Enable automatic reconnection
        reconnectionDelay: 1000, // Start with 1 second delay
        reconnectionDelayMax: 5000, // Maximum delay of 5 seconds
        reconnectionAttempts: Infinity, // Keep trying forever
        upgrade: true,
        rememberUpgrade: false,
        forceNew: true, // Force new connection to avoid sharing issues
        auth: socketAuth({ deviceId }),
      });
      console.log('🔌 Socket instance created:', !!this.socket);

      this.socket.on('connect', () => {
        console.log('✅ Connected to server');
        this.socket.emit('join-session', {
          sessionId: this.sessionId,
          clientType: this.clientType,
          participantId: this.participantId
        });
        
        // Start heartbeat to keep connection alive
        this.startHeartbeat();
      });

      this.socket.on('disconnect', (reason) => {
        console.log('⚠️ Disconnected from server:', reason);
        // Stop heartbeat when disconnected
        this.stopHeartbeat();
        
        if (reason === 'io server disconnect') {
          // Server disconnected us, manually reconnect
          console.log('🔄 Server disconnected, attempting to reconnect...');
          this.socket.connect();
        }
      });

      this.socket.on('reconnect', (attemptNumber) => {
        console.log('🔄 Reconnected after', attemptNumber, 'attempts');
        // Re-join session after reconnection
        this.socket.emit('join-session', {
          sessionId: this.sessionId,
          clientType: this.clientType,
          participantId: this.participantId
        });
        
        // Request current session state to sync any missed messages
        console.log('🔄 Requesting session state to sync missed messages...');
        this.socket.emit('get-session-state');
        
        // Restart heartbeat after reconnection
        this.startHeartbeat();
      });

      this.socket.on('reconnect_attempt', (attemptNumber) => {
        console.log('🔄 Reconnection attempt #', attemptNumber);
      });

      this.socket.on('connect_error', (error) => {
        console.error('❌ Connection error:', error.message);
        // The display checks whether a newer build is deployed: an old bundle's
        // handshake may be what the server refused (display utils/buildVersion.js).
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('kuutio:socket-connect-error'));
      });
    }

    return this.socket;
  }

  onSubmission(callback) {
    if (this.socket) {
      this.socket.on('new-submission', callback);
    }
  }

  onParticipantUpdate(callback) {
    if (this.socket) {
      this.socket.on('participant-update', callback);
    }
  }

  /**
   * A connection that only listens — no session joined, so no sessions row
   * and no instance room. For pages with no instance, such as the house
   * wall (/info), which reloads on 'info-board-changed'. Its own socket,
   * closed by the caller; the session socket above is untouched.
   */
  listen() {
    const socket = io(getConnectUrl(), {
      transports: ['polling', 'websocket'], reconnection: true, reconnectionDelay: 1000, reconnectionDelayMax: 5000, reconnectionAttempts: Infinity, forceNew: true,
      auth: socketAuth(),
    });
    return socket;
  }

  disconnect() {
    this.stopHeartbeat();
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  testConnectivity(url) {
    console.log('🔍 Testing connectivity to:', url);
    
    // Test 1: Basic HTTP request to backend
    fetch(url.replace('ws://', 'http://').replace('wss://', 'https://'))
      .then(response => response.json())
      .then(data => {
        console.log('✅ Backend HTTP reachable:', data);
      })
      .catch(error => {
        console.error('❌ Backend HTTP unreachable:', error);
      });
    
    // Test 2: Check network type (if available)
    if ('connection' in navigator) {
      console.log('🌐 Network info:', {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt
      });
    }
    
    // Test 3: Simple WebSocket test
    console.log('🔍 Testing WebSocket connection...');
    const testWs = io(url, { 
      transports: ['polling'], // Start with polling only
      timeout: 10000 
    });
    
    testWs.on('connect', () => {
      console.log('✅ WebSocket test connection successful');
      testWs.disconnect();
    });
    
    testWs.on('connect_error', (error) => {
      console.error('❌ WebSocket test connection failed:', error);
      testWs.disconnect();
    });
  }

  startHeartbeat() {
    // Stop any existing heartbeat
    this.stopHeartbeat();
    
    // Determine heartbeat frequency based on client type and page visibility
    const getHeartbeatInterval = () => {
      // Display apps need more frequent heartbeats since they're often inactive
      const isDisplay = this.clientType === 'display';
      // Check if page is hidden/backgrounded
      const isHidden = typeof document !== 'undefined' && document.hidden;
      
      if (isDisplay || isHidden) {
        return 15000; // 15 seconds for displays or hidden pages
      } else {
        return 30000; // 30 seconds for active tablets
      }
    };
    
    // Send ping to keep connection alive
    this.heartbeatInterval = setInterval(() => {
      if (this.socket && this.socket.connected) {
        const interval = getHeartbeatInterval();
        console.log(`💓 Sending heartbeat ping (${this.clientType}, interval: ${interval/1000}s)`);
        this.socket.emit('ping');
      }
    }, getHeartbeatInterval());
    
    // Listen for page visibility changes to adjust heartbeat
    if (typeof document !== 'undefined') {
      const handleVisibilityChange = () => {
        console.log(`👁️ Page visibility changed: ${document.hidden ? 'hidden' : 'visible'}`);
        // Restart heartbeat with new interval
        this.startHeartbeat();
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Store the listener so we can remove it later
      this.visibilityChangeListener = handleVisibilityChange;
    }
  }

  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      console.log('💔 Heartbeat stopped');
    }
    
    // Remove visibility change listener
    if (this.visibilityChangeListener && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityChangeListener);
      this.visibilityChangeListener = null;
    }
  }
}

export default new SocketService();